/**
 * `stats_strip` — the trust row under the hero (spec 42 R2; spec 28 R3).
 *
 * Two sources, never invented values:
 *  - `items` — figures the seller typed themselves (their facts).
 *  - otherwise, whichever store-profile fields are set (established year, team
 *    size, monthly capacity, city). The reference design hard-coded "50,000
 *    pcs" and spec 28 R3 calls that out as the anti-pattern — so nothing here
 *    is inferred, and zero tiles → the section renders nothing at all.
 *
 * An optional `heading` gives it a title using the same `.flint-section-head`
 * rhythm every other content section uses; the outer section owns no vertical
 * padding of its own so it matches the shared section spacing exactly.
 */
export interface StatItem {
  value: string;
  label: string;
}

export interface StoreProfile {
  establishedYear: number | null;
  teamSize: string | null;
  monthlyCapacity: string | null;
  city: string | null;
}

/** 60 → "60", 1.5 → "1.5", 1.0 → "1" — one decimal, no trailing zero. */
function short(v: number): string {
  return (Math.round(v * 10) / 10).toString();
}

/**
 * Compact a capacity figure so a big round number reads as "60k pcs", not
 * "60,000 pcs" — and a lakh doesn't spill five zeros across the tile. Only the
 * leading number is rewritten (60,000 → 60k, 1,00,000 → 100k, <1000 as-is);
 * any unit the seller typed ("pcs", "units/mo") is kept. Text that doesn't
 * start with digits ("made to order") is returned untouched. Applied to the
 * capacity tile only — never the established year, which is not a quantity.
 */
function compactCapacity(raw: string): string {
  const m = raw.trim().match(/^([\d,]+)(.*)$/);
  if (!m) return raw;
  const n = Number(m[1]!.replace(/,/g, ""));
  if (!Number.isFinite(n)) return raw;
  const unit = m[2]!.trim();
  const num =
    n >= 1_000_000 ? `${short(n / 1_000_000)}M` : n >= 1_000 ? `${short(n / 1_000)}k` : String(n);
  return unit ? `${num} ${unit}` : num;
}

/** Whichever profile fields are set, in the spec 28 R3 order. */
function tilesFromProfile(profile: StoreProfile): Array<{ value: string; caption: string }> {
  const tiles: Array<{ value: string; caption: string }> = [];
  if (profile.establishedYear) {
    tiles.push({ value: String(profile.establishedYear), caption: "Established" });
  }
  if (profile.teamSize) tiles.push({ value: profile.teamSize, caption: "Team size" });
  if (profile.monthlyCapacity) {
    tiles.push({ value: compactCapacity(profile.monthlyCapacity), caption: "Monthly capacity" });
  }
  if (profile.city) tiles.push({ value: profile.city, caption: "Ships from" });
  return tiles;
}

export function StatsStrip({
  profile,
  heading,
  items,
}: {
  profile: StoreProfile;
  heading?: string | undefined;
  items?: StatItem[] | undefined;
}) {
  // Authored figures win; only genuinely filled ones count so a half-typed tile
  // never reaches buyers. With none, fall back to the store profile.
  const authored = (items ?? [])
    .filter((t) => t.value.trim() && t.label.trim())
    .map((t) => ({ value: t.value, caption: t.label }));
  const tiles = authored.length > 0 ? authored : tilesFromProfile(profile);

  if (tiles.length === 0) return null;

  return (
    <section aria-label={heading || "About this manufacturer"}>
      {heading ? (
        <div className="flint-section-head">
          <h2
            style={{
              fontFamily: "var(--font-display)",
              fontWeight: 500,
              fontSize: "1.5rem",
              color: "var(--ink)",
              margin: 0,
            }}
          >
            {heading}
          </h2>
        </div>
      ) : null}
      <div className="flint-stats">
        {tiles.map((t, i) => (
          <div key={`${t.caption}-${i}`} className="flint-stats-tile">
            <div
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "clamp(1.875rem, 4vw, 2.75rem)",
                lineHeight: 1.05,
                letterSpacing: "-0.01em",
                color: "var(--ink)",
              }}
            >
              {t.value}
            </div>
            <div style={{ fontSize: "0.8125rem", color: "var(--muted)", marginTop: "0.375rem" }}>
              {t.caption}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
